import React, { useState, useRef, useEffect } from 'react';
import { IconX, IconBrandDiscord, IconLink, IconSend, IconHash, IconMessageCircle, IconUsers } from '@tabler/icons-react';

// Amplification campaigns always operate on per-group threads — the toggle only
// applies to the normal/REGULAR campaign flow where the default is the shared
// campaign channel.
const isAmplification = (campaign) => campaign?.campaignType === 'AMPLIFICATION';

const GoLiveModal = ({ campaign, group, creatorCount, posting, onPost, onClose }) => {
 const [briefUrl, setBriefUrl] = useState('');
 const groupSize = group?.groupSize || 10;
 const [message, setMessage] = useState(
 `Hey guys,\n\nWe have one more QRT campaign for you guys.\n\nWe need ${groupSize} QRT and ${groupSize} comment on this content.\n\nPlease make sure to reply to the comments on your QRT\n\nWe need this content ASAP`
 );
 const [channelMode, setChannelMode] = useState(group?.discord?.mode || 'shared');
 const briefInputRef = useRef(null);

 useEffect(() => {
 briefInputRef.current?.focus();
 }, []);

 const showChannelChoice = !isAmplification(campaign) && group?.status !== 'active';

 const handlePost = () => {
 onPost({
 briefUrl: briefUrl.trim() || undefined,
 message: message.trim() || undefined,
 channelMode: isAmplification(campaign) ? undefined : channelMode
 });
 };

 const channelOptions = [
 {
 value: 'shared',
 icon: IconUsers,
 title: 'Use shared campaign channel',
 description: 'Add creators to the existing campaign channel and role. Best when all groups talk together.'
 },
 {
 value: 'thread',
 icon: IconMessageCircle,
 title: 'Create separate Discord thread',
 description: 'Spin up a private thread on the campaign channel for this group only.'
 },
 {
 value: 'channel',
 icon: IconHash,
 title: 'Create separate Discord channel',
 description: 'Create a dedicated text channel for this group inside the campaign category.'
 }
 ];

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center">
 {/* Backdrop */}
 <div className="absolute inset-0 bg-black/50" onClick={onClose} />

 {/* Modal */}
 <div className="relative bg-surface-1 border border-glass-border w-full max-w-lg mx-4 overflow-hidden">
 {/* Header */}
 <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-glass-border">
 <div className="flex items-center gap-3">
 <div className="w-9 h-9 bg-accent-muted flex items-center justify-center">
 <IconBrandDiscord size={20} className="text-accent" />
 </div>
 <div>
 <h2 className="text-lg font-semibold text-text-primary">{group?.status === 'active' ? 'Post Campaign Brief' : 'Make Campaign Live'}</h2>
 <p className="text-xs text-text-secondary">
 {group.name} &middot; {creatorCount} creator{creatorCount !== 1 ? 's' : ''}
 </p>
 </div>
 </div>
 <button
 onClick={onClose}
 className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-glass-bg-hover"
 >
 <IconX size={18} />
 </button>
 </div>

 {/* Body */}
 <div className="px-4 sm:px-6 py-5 space-y-4">
 {/* Channel destination */}
 {showChannelChoice && (
 <div>
 <label className="block text-sm font-medium text-text-secondary mb-1.5">
 Where should this group go live?
 </label>
 <div className="space-y-2">
 {channelOptions.map(opt => {
 const Icon = opt.icon;
 const selected = channelMode === opt.value;
 return (
 <button
 key={opt.value}
 type="button"
 onClick={() => setChannelMode(opt.value)}
 className={`w-full flex items-start gap-3 px-3 py-2.5 border text-left transition-colors ${
 selected
 ? 'border-accent bg-accent-muted/20'
 : 'border-glass-border hover:border-accent/60 hover:bg-glass-bg-hover'
 }`}
 >
 <span className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 border ${selected ? 'border-accent' : 'border-glass-border'}`}>
 {selected && <span className="w-2 h-2 bg-accent" />}
 </span>
 <Icon size={18} className={selected ? 'text-accent flex-shrink-0' : 'text-text-tertiary flex-shrink-0'} />
 <div className="min-w-0 flex-1">
 <div className={`text-sm font-medium ${selected ? 'text-accent' : 'text-text-primary'}`}>
 {opt.title}
 </div>
 <div className="text-xs text-text-secondary mt-0.5">
 {opt.description}
 </div>
 </div>
 </button>
 );
 })}
 </div>
 </div>
 )}

 {/* Brief link */}
 <div>
 <label className="block text-sm font-medium text-text-secondary mb-1.5">
 Link to Brief
 <span className="text-text-tertiary font-normal ml-1">(optional)</span>
 </label>
 <div className="relative">
 <IconLink size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
 <input
 ref={briefInputRef}
 type="url"
 value={briefUrl}
 onChange={(e) => setBriefUrl(e.target.value)}
 placeholder="https://docs.google.com/..."
 className="w-full pl-9 pr-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 </div>
 </div>

 {/* Message */}
 <div>
 <label className="block text-sm font-medium text-text-secondary mb-1.5">
 Message
 </label>
 <textarea
 value={message}
 onChange={(e) => setMessage(e.target.value)}
 rows={7}
 className="w-full px-3 py-2 text-sm border border-glass-border bg-surface-1 text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
 />
 </div>

 {/* Preview */}
 <div>
 <label className="block text-xs font-medium text-text-secondary uppercase tracking-wider mb-1.5">
 Discord Preview
 </label>
 <div className="bg-[#313338] border border-gray-700 px-4 py-3 text-sm text-[#dbdee1] leading-relaxed max-h-48 overflow-y-auto">
 <p className="font-bold text-white mb-2">{campaign.name} — {group.name}</p>
 <div className="whitespace-pre-wrap">{message}</div>
 {briefUrl.trim() && (
 <p className="mt-3">
 {'📋 '}
 <span className="font-bold text-white">Campaign Brief:</span>
 {' '}
 <span className="text-[#00a8fc] underline">{briefUrl.trim()}</span>
 </p>
 )}
 </div>
 </div>
 </div>

 {/* Footer */}
 <div className="px-4 sm:px-6 py-4 bg-surface-1 border-t border-glass-border space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
 <p className="text-xs text-text-secondary">
 {showChannelChoice
 ? (channelMode === 'shared'
 ? 'Creators will be added to the shared campaign channel.'
 : channelMode === 'thread'
 ? 'A private thread will be created on the campaign channel.'
 : 'A new Discord channel will be created for this group.')
 : group?.status === 'active'
 ? 'The message will be posted in this group’s existing Discord destination.'
 : 'This will create a private Discord thread and post the message.'}
 </p>
 <div className="flex items-center gap-2 flex-shrink-0">
 <button
 onClick={onClose}
 disabled={posting}
 className="flex-1 sm:flex-initial px-4 py-2 text-sm font-medium border border-glass-border text-text-primary hover:bg-glass-bg-hover min-h-[44px]"
 >
 Cancel
 </button>
 <button
 onClick={handlePost}
 disabled={posting}
 className={`flex-1 sm:flex-initial px-4 sm:px-5 py-2 text-sm font-medium text-white flex items-center justify-center gap-2 min-h-[44px] ${
 posting
 ? 'bg-accent/60 cursor-not-allowed'
 : 'bg-accent hover:bg-accent-hover'
 }`}
 >
 <IconSend size={16} className={posting ? 'animate-pulse' : ''} />
 {posting ? 'Posting...' : group?.status === 'active' ? 'Post Brief' : 'Go Live'}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default GoLiveModal;
